/**
 * Delivery Estimates Impression Analytics
 */

import { useIsInViewport } from "@catchoftheday/utilities";

import { emitDeliveryEstimateImpression } from "../../events";

interface DeliveryEstimatesImpressionAnalyticsContext {
  labelType: "label" | "shortLabel";
  offerId?: string;
  postcode?: string;
}

export interface DeliveryEstimatesImpressionAnalyticsProps {
  /**
   * Enable delivery estimate impression tracking.
   * Typically, this is only enabled when the delivery estimate experiment is on.
   * @default false
   */
  enabled?: boolean;

  /**
   * Context which will be mapped and forwarded to `emitDeliveryEstimateImpression`.
   * @see emitDeliveryEstimateImpression
   */
  context?: DeliveryEstimatesImpressionAnalyticsContext;

  /**
   * Options for the underlying `useIsInViewport` hook.
   * @see useIsInViewport
   */
  options?: Omit<
    Parameters<typeof useIsInViewport>[0],
    "onComponentSeen" | "shouldTrackVisibility"
  >;
}

/**
 * Emits a delivery estimates impression event when the returned `ref` enters
 * the viewport.
 *
 * @example
 *
 */
export function useDeliveryEstimatesImpressionAnalytics({
  enabled = false,
  context,
  options,
}: DeliveryEstimatesImpressionAnalyticsProps) {
  // Emit the event when the ref enters the viewport.
  const handleComponentSeen = () => {
    // Provide some DX by warning the developer if they've enabled analytics but
    // not provided any context.
    if (process.env.NODE_ENV === "development") {
      if (enabled && !context) {
        console.warn(
          "You've enabled delivery estimate impression analytics but not provided any context."
        );
      }
    }

    // Do nothing if not enabled or missing options.
    if (!enabled || !context) return;

    const { offerId: property, postcode, labelType: label } = context;
    if (!property || !postcode) return; // Do nothing if missing required context values.

    // Convert postcode to a number.
    const value = Number(postcode); // TODO: Postcodes can't really be numbers (N.T. is 0800, for example). However the underlying `trackStructEvent` requires `value` to be a number.

    // Alrighty, let's emit the event.
    emitDeliveryEstimateImpression({ label, property, value });
  };

  const { ref } = useIsInViewport({
    shouldTrackVisibility: enabled,
    onComponentSeen: handleComponentSeen,
    ...options,
  });

  return {
    /** Apply `ref` to the element containing the delivery estimate label. */
    ref,
  };
}
